import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Pencil, Save, X, Plus, Trash2, ArrowUp, ArrowDown, BookOpen } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

const authCfg = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
});

export default function InstructionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/instructions`, authCfg());
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load instructions');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(data)));
    setEditMode(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditMode(false);
  };

  const saveEdit = async () => {
    if (draft.steps.some(s => !s.title.trim())) {
      toast.error('Every step needs a title');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/instructions`, draft, authCfg());
      setData(res.data);
      setEditMode(false);
      setDraft(null);
      toast.success('Instructions saved');
    } catch (e) {
      toast.error('Failed to save instructions');
    } finally {
      setSaving(false);
    }
  };

  const setStep = (i, field, value) => {
    setDraft(prev => {
      const steps = [...prev.steps];
      steps[i] = { ...steps[i], [field]: value };
      return { ...prev, steps };
    });
  };

  const moveStep = (i, dir) => {
    setDraft(prev => {
      const steps = [...prev.steps];
      const j = i + dir;
      if (j < 0 || j >= steps.length) return prev;
      [steps[i], steps[j]] = [steps[j], steps[i]];
      return { ...prev, steps };
    });
  };

  const removeStep = (i) => {
    setDraft(prev => ({ ...prev, steps: prev.steps.filter((_, idx) => idx !== i) }));
  };

  const addStep = () => {
    setDraft(prev => ({ ...prev, steps: [...prev.steps, { title: 'New Step', body: '' }] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;
  const view = editMode ? draft : data;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="instructions-page">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-accent" />
          {editMode ? (
            <Input
              value={draft.title}
              onChange={(e) => setDraft(p => ({ ...p, title: e.target.value }))}
              className="text-xl font-bold h-10 w-96"
              data-testid="instructions-title-input"
            />
          ) : (
            <h1 className="text-2xl font-bold text-foreground">{data.title}</h1>
          )}
        </div>
        {editMode ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="instructions-cancel-btn">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="instructions-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="instructions-edit-btn">
            <Pencil className="h-4 w-4 mr-1" /> Edit
          </Button>
        )}
      </div>

      {editMode ? (
        <Textarea
          value={draft.intro}
          onChange={(e) => setDraft(p => ({ ...p, intro: e.target.value }))}
          className="mb-6 text-sm"
          rows={2}
          data-testid="instructions-intro-input"
        />
      ) : (
        data.intro && <p className="text-sm text-muted-foreground mb-6">{data.intro}</p>
      )}

      <div className="space-y-3">
        {view.steps.map((step, i) => (
          <Card key={i} className="border border-border" data-testid={`instruction-step-${i}`}>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  {editMode ? (
                    <div className="space-y-2">
                      <Input
                        value={step.title}
                        onChange={(e) => setStep(i, 'title', e.target.value)}
                        className="font-semibold"
                        data-testid={`step-title-input-${i}`}
                      />
                      <Textarea
                        value={step.body}
                        onChange={(e) => setStep(i, 'body', e.target.value)}
                        rows={4}
                        className="text-sm"
                        data-testid={`step-body-input-${i}`}
                      />
                    </div>
                  ) : (
                    <>
                      <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">{step.body}</p>
                    </>
                  )}
                </div>
                {editMode && (
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveStep(i, -1)} disabled={i === 0} title="Move up" data-testid={`step-up-${i}`}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveStep(i, 1)} disabled={i === view.steps.length - 1} title="Move down" data-testid={`step-down-${i}`}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => removeStep(i)} title="Delete step" data-testid={`step-delete-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {editMode && (
        <Button variant="outline" className="mt-4 w-full border-dashed" onClick={addStep} data-testid="instructions-add-step-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Step
        </Button>
      )}

      {!editMode && data.updated_at && (
        <p className="text-xs text-muted-foreground mt-6">Last updated {new Date(data.updated_at).toLocaleDateString()}</p>
      )}
    </div>
  );
}
